import React, { useEffect } from "react";
import { Modal } from "../../SharedComponents";
import { ModalNames } from "../../../Constants/modalNames";
import { Div, HasLoading, Icon, Text } from "../../Kit";
import { TokenDetailsItem } from "../TokenDetailsItem/TokenDetailsItem";
import { Award, CloseCircle, Danger, Home3, InfoCircle } from "iconsax-react";
import { useLanguage } from "../../../Store/modules/language";
import { Button } from "../../Kit/Button";
import { useModal } from "../../../Store/modules/modal/modal";
import { useGetUserTokenHistory } from "../../../Store/modules/ai/getUserTokenHistory/getUserTokenHistory";
import { Trans } from "react-i18next";
import i18n from "../../../i18next";
import styled from "../../../StyleSheet/styled-components";
import { LineChart } from "../../Charts/LineChart/LineChart";
import { PieChart } from "../../Charts/PieChart/PieChart";
import { separate } from "../../../Utils";
import { useLanguageNavigate } from "../../../Hooks/useLanguageNavigate";
import { Pages } from "../../../Constants/pages";

export const TokenHistoryDetailsModal: React.FC = () => {
  const { direction } = useLanguage();
  const { dispatchCloseModal, modalProps, name } = useModal();
  const { dispatchGetUserTokenHistory, getUserTokenHistoryData, getUserTokenHistoryLoading } =
    useGetUserTokenHistory();
  let { id, title, tokens, router } = modalProps;

  useEffect(() => {
    if (id && ModalNames.TokenHistoryDetailsModal === name) {
      dispatchGetUserTokenHistory({ id });
    }
  }, [dispatchGetUserTokenHistory, id]);

  let { usageTokens, token_details, subsidized_price, full_price, extra_token_price } =
    getUserTokenHistoryData?.data || {};
  const labels = usageTokens?.map((item) => item.week_start);
  const usageTokensList = usageTokens?.map((item) => parseFloat(item.usage_tokens));

  const StyledTable = styled.table`
    border: 1px solid ${({ theme }) => theme.tertiary};
    border-collapse: separate;
    border-spacing: 0;
    overflow: hidden;
    width: 100%;

    thead {
      background-color: ${({ theme }) => theme.lightForth};
    }

    tbody {
      background-color: ${({ theme }) => theme.white};
    }

    th {
      border: 1px solid ${({ theme }) => theme.tertiary};
    }
    td {
      border: 1px solid ${({ theme }) => theme.tertiary};
    }

    tbody td:last-child span:last-child {
      color: red;
    }

    tbody td span:first-child {
      text-align: start;
      max-width: 135px;
    }
  `;

  return (
    <Modal
      modalName={ModalNames.TokenHistoryDetailsModal}
      preventToClose
      modalClassName="rounded-[10px] w-[950px] h-[590px]"
    >
      <Div
        bg="secondary"
        className="w-full h-[45px] flex items-center rounded-[5px] px-[15px] justify-between"
      >
        <Text elementType="p" className="text-sm" translatable={false}>
          {title} {tokens}{" "}
          <Text elementType="span" className="text-sm">
            token
          </Text>
        </Text>
        <Button
          title=""
          iconColor="text"
          StartIconComponent={CloseCircle}
          onClick={dispatchCloseModal}
        />
      </Div>
      <HasLoading loading={getUserTokenHistoryLoading}>
        <div className="flex flex-col lg:flex-row justify-between mt-[23px]" dir={direction}>
          <div className="w-full lg:w-[60%]">
            <div className="flex items-center mb-[15px]">
              <Icon Component={Danger} size={14} color="iconColor" />
              <Text elementType="span" color="iconColor" className="text-[12px] ms-1">
                tokenHistory.autoRefreshNote
              </Text>
            </div>

            <Text elementType="div" translatable={false}>
              <Trans
                i18n={i18n}
                i18nKey="tokenHistory.table"
                values={{
                  usage_wallet_subsidized: separate(token_details?.usage_wallet_subsidized),
                  usage_free: separate(token_details?.usage_free),
                  purchase_extra_tokens: separate(token_details?.purchase_extra_tokens),
                  usage_subscription: separate(token_details?.usage_subscription),
                  total_tokens: separate(token_details?.total_tokens),
                }}
                components={{
                  table: (
                    <StyledTable className="text-sm text-center rtl w-full border rounded-[5px]" />
                  ),
                  thead: <thead />,
                  tbody: <tbody />,
                  tr: <tr className="border-b-[1px]" />,
                  th: <th className="font-medium py-2" />,
                  td: <td className="align-middle py-3" />,
                  div: (
                    <div className="flex flex-col items-center md:flex-row md:justify-between md:px-2" />
                  ),
                  span: <span className="flex items-center mt-1 md:mt-0 font-[350] text-[13px]" />,
                  br: <br />,
                  TokenIcon: (
                    <Icon Component={Home3} color="tokenIcon" size={15} className="ms-1" />
                  ),
                }}
              />
            </Text>

            {labels?.length ? (
              <div className="mt-[20px]">
                <Text elementType="span" className="text-sm">
                  tokenHistory.weeklyTokenUsage
                </Text>
                <Div border className="mt-2" />
                <LineChart height="215px" data={usageTokensList || []} labels={labels || []} />
              </div>
            ) : null}
          </div>
          <div className="hidden lg:block mx-[12px]" />
          <div className="w-full lg:w-[40%] mt-6 lg:mt-0">
            <Div bg="secondary" className="py-[12px] px-[15px]">
              <div className="flex justify-between">
                <Text elementType="span" className="text-sm">
                  tokenHistory.usageAmount
                </Text>
                <Icon Component={Award} color="tokenIcon" size={20} />
              </div>
              <div style={{ height: "167px" }}>
                {token_details && (
                  <PieChart
                    cached_tokens={token_details.cached_tokens}
                    input_tokens={token_details.input_tokens}
                    output_tokens={token_details.output_tokens}
                  />
                )}
              </div>
            </Div>
            <div className="space-y-2 mt-[20px]">
              <div className="flex items-center">
                <Text elementType="span" className="text-sm">
                  tokenHistory.tokenCosts
                </Text>
                <Icon
                  Component={InfoCircle}
                  color="iconColor"
                  size={18}
                  className="mx-2"
                  onClick={() => {
                    dispatchCloseModal();
                    router(`${Pages.TokenUsageDetails}#tokenPricingStructure`);
                  }}
                />
              </div>

              <Div border className="mt-2" />
              <TokenDetailsItem
                title="tokenHistory.planTokenCosts"
                value={extra_token_price}
                IconComponent={Home3}
                lessThan
              />
              <TokenDetailsItem
                title="tokenHistory.subscriptionTokenCosts"
                value={subsidized_price}
                IconComponent={Home3}
              />
              <TokenDetailsItem
                title="tokenHistory.freeTokenCosts"
                value={full_price}
                IconComponent={Home3}
              />
              <TokenDetailsItem
                title="tokenHistory.extraToken"
                value={extra_token_price}
                IconComponent={Home3}
              />
            </div>
          </div>
        </div>
      </HasLoading>
    </Modal>
  );
};
